// Role-based access control utilities

export type UserRole = 'user' | 'admin' | 'super_admin';

export interface RolePermissions {
  canAccessDashboard: boolean;
  canAccessFlashCheck: boolean;
  canAccessAdminPanel: boolean;
  canAccessSuperAdmin: boolean;
}

// Role hierarchy: user < admin < super_admin
export const roleHierarchy: Record<UserRole, number> = {
  'user': 0,
  'admin': 1,
  'super_admin': 2
};

/**
 * Default permissions for unauthenticated or loading states
 * Uses most restrictive permissions until role is verified
 */
export const DEFAULT_PERMISSIONS: RolePermissions = {
  canAccessDashboard: true,    // Everyone can see dashboard
  canAccessFlashCheck: true,   // Everyone can see flashcheck
  canAccessAdminPanel: false,  // Hide admin content until role verified
  canAccessSuperAdmin: false   // Hide super admin content until role verified
};

/**
 * Creates a permissions object with all permissions set to false
 * Automatically adapts when new permissions are added to RolePermissions interface
 */
export const createNoPermissions = (): RolePermissions => {
  // Get a sample permissions object and set all values to false
  const samplePermissions = getRolePermissions('user');
  const noPermissions: RolePermissions = {} as RolePermissions;
  
  // Dynamically set all permission keys to false
  (Object.keys(samplePermissions) as (keyof RolePermissions)[]).forEach(key => {
    noPermissions[key] = false;
  });
  
  return noPermissions;
};

/**
 * Check if user has sufficient role level for a required role
 */
export const hasRole = (userRole: UserRole | string, requiredRole: UserRole): boolean => {
  const userRoleLevel = roleHierarchy[userRole as UserRole] ?? -1;
  const requiredRoleLevel = roleHierarchy[requiredRole];
  return userRoleLevel >= requiredRoleLevel;
};

/**
 * Get permissions for a user based on their role
 */
export const getRolePermissions = (userRole: UserRole | string): RolePermissions => {
  const role = userRole as UserRole;
  
  return {
    canAccessDashboard: true, // All users can access dashboard
    canAccessFlashCheck: true, // All users can access FlashCheck
    canAccessAdminPanel: hasRole(role, 'admin'), // Only admin and super_admin
    canAccessSuperAdmin: hasRole(role, 'super_admin') // Only super_admin
  };
};

/**
 * Check if user is from IntuitionAI domain (required for super admin access)
 */
export const isIntuitionAIUser = (email: string): boolean => {
  return email.endsWith('@intuitionai.co');
};

/**
 * Get user navigation items based on role and domain
 */
export const getUserNavigationItems = (
  userRole: UserRole | string, 
  userEmail: string,
  currentPath: string
) => {
  const permissions = getRolePermissions(userRole);
  const isIntuitionAI = isIntuitionAIUser(userEmail);
  
  const items = [
    {
      path: '/dashboard',
      icon: '🏠',
      label: 'Dashboard',
      active: currentPath === '/dashboard',
      visible: permissions.canAccessDashboard
    },
    {
      path: '/flashcheck',
      icon: '⚡',
      label: 'FlashCheck',
      active: currentPath === '/flashcheck',
      visible: permissions.canAccessFlashCheck
    },
    {
      path: '/admin',
      icon: '⚙️',
      label: 'Admin Panel',
      active: currentPath === '/admin',
      visible: permissions.canAccessAdminPanel
    },
    {
      path: '/superadmin',
      icon: '🚀',
      label: 'Super Admin',
      active: currentPath === '/superadmin',
      visible: permissions.canAccessSuperAdmin && isIntuitionAI
    }
  ];
  
  return items.filter(item => item.visible);
};

// Example: If we later add a new permission like 'canAccessReporting'
// to the RolePermissions interface, createNoPermissions() will 
// automatically include it and set it to false, without requiring
// any manual updates to the function.

/**
 * Example of adding a new permission (commented out for now):
 * 
 * export interface RolePermissions {
 *   canAccessDashboard: boolean;
 *   canAccessFlashCheck: boolean;
 *   canAccessAdminPanel: boolean;
 *   canAccessSuperAdmin: boolean;
 *   canAccessReporting: boolean;  // <-- New permission
 * }
 * 
 * createNoPermissions() would automatically return:
 * {
 *   canAccessDashboard: false,
 *   canAccessFlashCheck: false,
 *   canAccessAdminPanel: false,
 *   canAccessSuperAdmin: false,
 *   canAccessReporting: false  // <-- Automatically included!
 * }
 */
